import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  exchangeCodeForTokensOnce,
  fetchUserInfo,
  fetchXTranslatorAccess,
  refreshAccessToken,
  savePreferredLanguage,
  signOutAndReturn,
  startSepmSignIn,
} from "./oauth";

const SESSION_KEY = "sepm_docs_session";
const AuthContext = createContext(null);

function readStoredSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.accessToken || !session?.user?.email) return null;
    return session;
  } catch {
    return null;
  }
}

function writeStoredSession(session) {
  if (!session) localStorage.removeItem(SESSION_KEY);
  else localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

function sessionFromToken(token, user, previous) {
  const expiresIn = Number(token.expires_in) || 3600;
  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token || previous?.refreshToken || "",
    expiresAt: Date.now() + expiresIn * 1000,
    user,
    xTranslator: previous?.xTranslator || null,
  };
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  const sessionRef = useRef(null);
  sessionRef.current = session;

  const persist = useCallback((next) => {
    sessionRef.current = next;
    setSession(next);
    writeStoredSession(next);
  }, []);

  const ensureFreshSession = useCallback(async (current) => {
    if (!current?.accessToken) return null;
    if (current.expiresAt && current.expiresAt - Date.now() > 60_000) return current;
    if (!current.refreshToken) return null;
    const token = await refreshAccessToken(current.refreshToken);
    const user = await fetchUserInfo(token.access_token);
    const next = sessionFromToken(token, user, current);
    persist(next);
    return next;
  }, [persist]);

  const refreshAccess = useCallback(async () => {
    const active = sessionRef.current || readStoredSession();
    if (!active?.user?.userId) return null;
    let fresh = active;
    try {
      fresh = (await ensureFreshSession(active)) || active;
    } catch {
      persist(null);
      return null;
    }
    try {
      const access = await fetchXTranslatorAccess(fresh.accessToken, fresh.user.userId);
      const next = { ...fresh, xTranslator: access };
      persist(next);
      return access;
    } catch (error) {
      if (error?.status === 401) {
        persist(null);
        return null;
      }
      if (fresh.xTranslator?.active) return fresh.xTranslator;
      const unavailable = {
        active: false,
        unavailable: true,
        planName: "",
        status: "",
        checkedAt: Date.now(),
      };
      persist({ ...fresh, xTranslator: unavailable });
      return unavailable;
    }
  }, [ensureFreshSession, persist]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = readStoredSession();
      if (!stored) {
        if (!cancelled) setReady(true);
        return;
      }
      try {
        const fresh = await ensureFreshSession(stored);
        if (cancelled) return;
        const next = fresh || stored;
        let withAccess = next;
        try {
          const access = await fetchXTranslatorAccess(next.accessToken, next.user.userId);
          withAccess = { ...next, xTranslator: access };
        } catch (error) {
          if (error?.status === 401) {
            if (!cancelled) persist(null);
            return;
          }
        }
        if (!cancelled) persist(withAccess);
      } catch {
        if (!cancelled) persist(null);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ensureFreshSession, persist]);

  const completeSignIn = useCallback(async ({ code, state }) => {
    const token = await exchangeCodeForTokensOnce({ code, state });
    const user = await fetchUserInfo(token.access_token);
    const next = sessionFromToken(token, user, null);
    try {
      next.xTranslator = await fetchXTranslatorAccess(next.accessToken, user.userId);
    } catch (error) {
      if (error?.status === 401) throw error;
      next.xTranslator = {
        active: false,
        unavailable: true,
        planName: "",
        status: "",
        checkedAt: Date.now(),
      };
    }
    persist(next);
    return next;
  }, [persist]);

  const signIn = useCallback((options) => startSepmSignIn(options), []);

  const signOut = useCallback(() => {
    const token = session?.accessToken;
    persist(null);
    signOutAndReturn(token);
  }, [persist, session]);

  const saveLanguage = useCallback(async (language) => {
    const active = sessionRef.current;
    if (!active?.accessToken) return;
    let fresh = active;
    try {
      fresh = (await ensureFreshSession(active)) || active;
    } catch {
      return;
    }
    try {
      await savePreferredLanguage(fresh.accessToken, language);
    } catch {
      return;
    }
    persist({
      ...fresh,
      user: { ...fresh.user, preferredLanguage: language },
    });
  }, [ensureFreshSession, persist]);

  const value = useMemo(
    () => ({
      ready,
      user: session?.user || null,
      xTranslator: session?.xTranslator || null,
      xTranslatorAccess: Boolean(session?.xTranslator?.active),
      signIn,
      signOut,
      completeSignIn,
      refreshAccess,
      saveLanguage,
    }),
    [ready, session, signIn, signOut, completeSignIn, refreshAccess, saveLanguage]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
